import ApiClient from "../ApiClient";
import { mapRawStageToAccountStage } from "../constants/accountStages";

const dateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function normalizeAccountValue(value) {
  if (value == null || value === "") return "-";
  return value;
}

function formatAccountDate(value) {
  if (!value || value === "-") return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return dateFormatter.format(date);
}

function formatCashInflow(value) {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  if (Number.isFinite(n)) {
    return `₹${n.toLocaleString("en-IN")}`;
  }
  const s = String(value).trim();
  if (!s || s === "-") return "-";
  return s.includes("₹") ? s : `₹${s}`;
}

function normalizeAccount(account, index = 0) {
  const primaryId = account?.id ?? account?.Id;
  const accountIdentifier = account?.accountId ?? account?.AccountId;
  const fallbackKey = [
    account?.accountName ?? account?.AccountName ?? account?.company,
    account?.accountDomainName ?? account?.AccountDomainName ?? account?.domain,
    account?.activeFrom ?? account?.ActiveFrom,
    account?.activeTo ?? account?.ActiveTo,
    account?.revenue ?? account?.Revenue,
    index,
  ]
    .filter((value) => value != null && value !== "")
    .join("-");

  const accountId = String(
    primaryId ?? accountIdentifier ?? fallbackKey ?? `account-${index + 1}`,
  );

  const rawStage =
    account?.accountStage ??
    account?.AccountStage ??
    account?.stage ??
    account?.Stage ??
    account?.accountStatus ??
    account?.AccountStatus;

  const revenueRaw =
    account?.Revenue ??
    account?.revenue ??
    account?.AnnualRevenue ??
    account?.annualRevenue ??
    account?.Turnover ??
    account?.turnover ??
    account?.cashInflow ??
    account?.CashInflow;

  return {
    rowKey: accountId,
    accountId,
    company: normalizeAccountValue(
      account?.Company ??
        account?.company ??
        account?.AccountName ??
        account?.accountName ??
        account?.Name ??
        account?.name,
    ),
    domain: normalizeAccountValue(
      account?.Domain ??
        account?.domain ??
        account?.accountDomainName ??
        account?.AccountDomainName ??
        account?.Website ??
        account?.website ??
        account?.WebSite ??
        account?.AccountDomain ??
        account?.accountDomain,
    ),
    activeFrom: formatAccountDate(
      account?.activeFrom ?? account?.ActiveFrom ?? account?.activeFromDate,
    ),
    activeTo: formatAccountDate(
      account?.activeTo ?? account?.ActiveTo ?? account?.activeToDate,
    ),
    revenue: normalizeAccountValue(revenueRaw),
    cashInflow: formatCashInflow(revenueRaw),
    joined: formatAccountDate(
      account?.joinedOn ??
        account?.JoinedOn ??
        account?.createdOn ??
        account?.CreatedOn ??
        account?.activeFrom ??
        account?.ActiveFrom,
    ),
    stage: mapRawStageToAccountStage(rawStage),
    contactName: normalizeAccountValue(
      account?.contactName ??
        account?.ContactName ??
        account?.primaryContact ??
        account?.PrimaryContact ??
        account?.contactPerson ??
        account?.ContactPerson,
    ),
    email: normalizeAccountValue(
      account?.email ?? account?.Email ?? account?.contactEmail ?? account?.ContactEmail,
    ),
    phone: normalizeAccountValue(
      account?.phone ??
        account?.Phone ??
        account?.phoneNumber ??
        account?.PhoneNumber ??
        account?.whatsAppNo ??
        account?.WhatsAppNo,
    ),
    source: normalizeAccountValue(
      account?.source ?? account?.Source ?? account?.leadSource ?? account?.LeadSource,
    ),
    linkedLead: normalizeAccountValue(
      account?.linkedLead ?? account?.LinkedLead ?? account?.leadName ?? account?.LeadName,
    ),
    transactions: Array.isArray(account?.transactions)
      ? account.transactions
      : Array.isArray(account?.Transactions)
        ? account.Transactions
        : [],
    activities: Array.isArray(account?.activities)
      ? account.activities
      : Array.isArray(account?.Activities)
        ? account.Activities
        : [],
  };
}

function normalizeCursorResponse(response) {
  const payload = response?.data?.data ?? response?.data ?? {};
  const accountItems =
    payload?.accountDetails ??
    payload?.accounts ??
    payload?.items ??
    payload?.data ??
    payload?.records ??
    [];

  return {
    data: Array.isArray(accountItems)
      ? accountItems.map((account, index) => normalizeAccount(account, index))
      : [],
    nextCursor:
      payload?.nextCursor ??
      payload?.cursor ??
      payload?.pagination?.nextCursor ??
      null,
  };
}

const AccountsService = {
  async getAccountsCursor({ limit = 20, cursor = null, search = "" } = {}) {
    const response = await ApiClient.get(`/Account/accounts-cursor`, {
      params: {
        limit,
        ...(cursor ? { cursor } : {}),
        ...(search ? { search } : {}),
      },
    });

    return normalizeCursorResponse(response);
  },

  /** Load all pages (up to maxRows) for Kanban. */
  async getAllAccounts({ search = "", pageSize = 50, maxRows = 500 } = {}) {
    const rows = [];
    let cursor = null;
    while (rows.length < maxRows) {
      const { data, nextCursor } = await AccountsService.getAccountsCursor({
        limit: pageSize,
        cursor,
        search,
      });
      rows.push(...data);
      if (!nextCursor || !data.length) break;
      cursor = nextCursor;
    }
    return rows;
  },

  /**
   * Persist stage when API exists. Swallows errors so UI stays usable if endpoint is missing.
   */
  async updateAccountStage({ accountId, accountStage }) {
    try {
      await ApiClient.patch(
        `/Account/accounts/${encodeURIComponent(accountId)}/stage`,
        { accountStage },
      );
      return { ok: true, synced: true };
    } catch (e) {
      console.warn("[AccountsService] updateAccountStage skipped:", e?.message || e);
      return { ok: true, synced: false };
    }
  },
};

export default AccountsService;
